import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { Users, MoreHorizontalIcon, Pen } from 'lucide-react';
import Page from 'components/shared/Page';
import HistoricalCampaignStrip from 'components/shared/HistoricalCampaignStrip';
import { useAGLOCampaign, useCampaignSwitchStatus } from 'hooks/useAGLOCampaign';
import EmptyCard from 'components/shared/EmptyCard';
import { toast } from 'sonner';
import { useAPI, useDebounce } from 'hooks';
import { PreferencesContext } from 'context';
import { formatDate, getUserName, preventSubmitWithENTER } from 'helpers';
import { defaultCategories, NpiCheckSuccessIcon, NpiCheckFailIcon, NpiCheckPendingIcon } from 'data';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { cn } from 'lib/utils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from 'components/ui/table';
import { Badge } from 'components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from 'components/ui/select';
import AssignConfirmationModal from './components/AssignConfirmationModal';
import AgencySelectionModal from './components/AgencySelectionModal';
import { Button } from 'components/ui/button';
import { ButtonGroup } from 'components/ui/button-group';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { Input } from 'components/ui/input';
import { Checkbox } from 'components/ui/checkbox';
import EditTripModal from '../Trip/components/EditTripModal';

const AgencyAssignation = () => {
  const campaign = useAGLOCampaign();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 1000);
  return (
    <AgencyAssignationForCampaign
      key={campaign.scope.join(':')}
      campaign={campaign}
      search={search}
      setSearch={setSearch}
      debouncedSearch={debouncedSearch}
    />
  );
};

const AgencyAssignationForCampaign = ({ campaign, search, setSearch, debouncedSearch }) => {
  const { params: campaignParams, scope: campaignScope, isReadOnly, isHistoricalCampaign } = campaign;
  const { api } = useAPI();
  const { registrationDeposit, pilgrimEligibilityMinAge } = useContext(PreferencesContext);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, pilgrim: null, agency: null });
  const [agencySelectionModal, setAgencySelectionModal] = useState(false);
  const [selectedPilgrims, setSelectedPilgrims] = useState([]);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeGroupIfu, setActiveGroupIfu] = useState(null);
  const [pagination, setPagination] = useState({});
  const itemsPerPage = 20;
  const queryClient = useQueryClient();

  const tripQuery = useQuery({
    queryFn: api.list('trip/previous_campaign/reported/', campaignParams),
    queryKey: ['trip/previous_campaign/reported/', ...campaignScope, campaignParams],
    keepPreviousData: false,
    refetchOnMount: campaign.isCampaignSwitching ? 'always' : false,
  });
  const { data: tripPerAgency } = tripQuery;
  useCampaignSwitchStatus([tripQuery]);

  const { data: agenciesData } = useQuery({
    queryFn: api.list('agency/'),
    queryKey: ['agency/'],
    enabled: !!tripPerAgency && !isReadOnly,
  });

  const { data: categoriesData } = useQuery({
    queryFn: api.list('category/'),
    queryKey: ['category/'],
    enabled: !!tripPerAgency && !isReadOnly,
  });

  // only display active agencies
  const agencies = agenciesData
    ? agenciesData.filter((agency) => agency.admin?.is_verified && agency.admin?.is_active)
    : [];

  const categories = Array.isArray(categoriesData)
    ? [...categoriesData].filter((category) => defaultCategories.includes(category.name)).reverse() // Remove 'Encadreur' categories
    : categoriesData;

  const filterTripsBySearch = (trips) => {
    if (!debouncedSearch) return trips;

    const searchValue = debouncedSearch.toLowerCase();

    return trips.filter((trip) => {
      const fullName = `${trip.pilgrim.first_name} ${trip.pilgrim.last_name}`.toLowerCase();
      const npi = trip.pilgrim.npi?.toLowerCase() || '';

      return fullName.includes(searchValue) || npi.includes(searchValue);
    });
  };

  const { mutate: assignAgency, isLoading } = useMutation({
    meta: { agloCampaignWrite: true },
    mutationFn: ({ pilgrimIds, agencyId }) => api.post(`trip/assign_agency/${agencyId}/`, pilgrimIds),
    onSuccess: ({ data }) => {
      const success = data.successful_transfers;
      const failures = data.errors;

      queryClient.invalidateQueries({ queryKey: ['trip/previous_campaign/reported/'] });

      if (success && success.length > 0) {
        toast.success(`Assignation réussi pour ${success.length} pèlerin(s)`);
      }

      if (failures && failures.length > 0) {
        if (failures.length > 3) {
          toast.error(`Échec de l'assignation pour ${failures.length} pèlerins.`);
        } else {
          failures.forEach((failure) => {
            toast.error(`Échec de l'assignation pour: ${failure.pilgrim}. Cause : ${failure.error}`);
          });
        }
      }

      setConfirmDialog({ open: false, pilgrim: null, agency: null });
      setAgencySelectionModal(false);
      setSelectedPilgrims([]);
    },
  });

  const handleSelectAgency = (pilgrim, agencyId) => {
    if (isReadOnly) return;
    const agency = agencies.find((a) => a.id === agencyId);
    setConfirmDialog({ open: true, pilgrim, agency });
  };

  const handleAgencySelectionModal = (data) => {
    if (isReadOnly) return;
    const pilgrimIds = data.trips.map((trip) => trip.id);
    setSelectedPilgrims(pilgrimIds);
    setAgencySelectionModal(true);
  };

  const confirmAssignment = () => {
    if (isReadOnly || isLoading) return;
    const { pilgrim, agency } = confirmDialog;
    assignAgency({ pilgrimIds: [pilgrim.id], agencyId: agency.id });
  };

  const handleEditPilgrim = (pilgrim) => {
    if (isReadOnly) return;
    setSelectedItem(pilgrim);
    setOpenEditModal(true);
  };

  const getPageItems = (agencyIfu, trips) => {
    const currentPage = pagination[agencyIfu] || 1;

    const indexOfLast = currentPage * itemsPerPage;
    const indexOfFirst = indexOfLast - itemsPerPage;

    return trips.slice(indexOfFirst, indexOfLast);
  };

  const changePage = (agencyIfu, page, totalPages) => {
    if (page < 1 || page > totalPages) return;

    setPagination((prev) => ({
      ...prev,
      [agencyIfu]: page,
    }));
  };

  const togglePilgrimSelection = (pilgrimId, groupIfu) => {
    // If selecting from a new group, reset previous selection
    if (activeGroupIfu !== groupIfu) {
      setActiveGroupIfu(groupIfu);
      setSelectedPilgrims([pilgrimId]);
      return;
    }

    setSelectedPilgrims((prev) =>
      prev.includes(pilgrimId) ? prev.filter((id) => id !== pilgrimId) : [...prev, pilgrimId]
    );
  };

  const toggleAllInGroup = (groupTrips, groupIfu) => {
    const groupIds = groupTrips.map((t) => t.id);
    const isAlreadyActiveGroup = activeGroupIfu === groupIfu;
    const allInGroupSelected = isAlreadyActiveGroup && groupIds.every((id) => selectedPilgrims.includes(id));

    if (allInGroupSelected) {
      setSelectedPilgrims([]);
      setActiveGroupIfu(null);
    } else {
      setActiveGroupIfu(groupIfu);
      setSelectedPilgrims(groupIds);
    }
  };

  return (
    <Page
      title="Assignation des Pèlerins"
      description={
        isHistoricalCampaign
          ? 'Consultez les pèlerins reportés de cette campagne.'
          : 'Attribuez chaque pèlerin à une agence agréée'
      }
      headerContent={<HistoricalCampaignStrip />}
      removeContentStyles
    >
      {(!tripPerAgency || tripPerAgency.length === 0) && (
        <EmptyCard className="bg-white rounded-sm border border-solid border-gray-200" />
      )}

      {tripPerAgency && tripPerAgency.length > 0 && (
        <>
          <div className="flex gap-2 mb-4 rounded-sm border border-gray-200 p-4">
            <Input
              placeholder="Rechercher un pèlerin (Nom, Prénoms, NPI)"
              className="w-full rounded-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={preventSubmitWithENTER}
            />
          </div>

          <div className="flex flex-col gap-6">
            {tripPerAgency &&
              tripPerAgency.map((data) => {
                const filteredTrips = filterTripsBySearch(data.trips);

                // Hide empty tables after search
                if (filteredTrips.length === 0) {
                  return null;
                }

                const currentPage = pagination[data.agency_ifu] || 1;
                const totalPages = Math.ceil(filteredTrips.length / itemsPerPage);

                return (
                  <div key={data.agency_ifu} className="flex flex-col gap-4">
                    <div className="flex items-center justify-between gap-2">
                      <h2 className="text-lg">
                        Pèlerin de la société <span className="text-hajj-primary font-bold">{data.agency_name}</span>
                      </h2>

                      {!isReadOnly && (
                        <div className="flex gap-2">
                          {/* CASE 1: Items are selected in THIS specific table */}
                          {activeGroupIfu === data.agency_ifu && selectedPilgrims.length > 0 ? (
                            <>
                              <Button variant="hajj" size="sm" onClick={() => setAgencySelectionModal(true)}>
                                <Users className="mr-2 h-4 w-4" />
                                Assigner la sélection ({selectedPilgrims.length})
                              </Button>

                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedPilgrims([]);
                                  setActiveGroupIfu(null);
                                }}
                              >
                                Annuler
                              </Button>
                            </>
                          ) : (
                            /* CASE 2: No selection, show the "Tout Assigner" button */
                            <Button variant="hajj" size="sm" onClick={() => handleAgencySelectionModal(data)}>
                              <Users className="mr-2 h-4 w-4" />
                              Tout Assigner ({filteredTrips.length})
                            </Button>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="rounded-sm overflow-hidden border border-gray-200">
                      <Table className="overflow-x-auto">
                        <TableHeader className="bg-gray-100">
                          <TableRow>
                            {!isReadOnly && (
                              <TableHead className="w-[50px]">
                                <Checkbox
                                  checked={
                                    filteredTrips.length > 0 &&
                                    filteredTrips.every((t) => selectedPilgrims.includes(t.id))
                                  }
                                  onCheckedChange={() => toggleAllInGroup(filteredTrips)}
                                />
                              </TableHead>
                            )}
                            <TableHead>Pèlerins</TableHead>
                            <TableHead>NPI</TableHead>
                            <TableHead>Ancienne agence</TableHead>
                            {!isReadOnly && <TableHead>Agence actuelle</TableHead>}
                            <TableHead>Date d&apos; inscription</TableHead>
                            {!isReadOnly && <TableHead>Action</TableHead>}
                          </TableRow>
                        </TableHeader>

                        <TableBody>
                          {getPageItems(data.agency_ifu, filteredTrips).map((pilgrim) => {
                            const isSelected =
                              activeGroupIfu === data.agency_ifu && selectedPilgrims.includes(pilgrim.id);

                            return (
                              <TableRow
                                key={pilgrim.id}
                                className={cn('hover:bg-gray-50', isSelected && 'bg-hajj-primary/5')}
                              >
                                {!isReadOnly && (
                                  <TableCell>
                                    <Checkbox
                                      // Clear checkbox if it's from a different group
                                      checked={isSelected}
                                      onCheckedChange={() => togglePilgrimSelection(pilgrim.id, data.agency_ifu)}
                                    />
                                  </TableCell>
                                )}
                                <TableCell>
                                  {getUserName(pilgrim.pilgrim.first_name, pilgrim.pilgrim.last_name)}
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    {pilgrim.pilgrim.npi}

                                    {pilgrim.pilgrim.is_npi_valid && <NpiCheckSuccessIcon />}

                                    {pilgrim.pilgrim.is_npi_valid === false && <NpiCheckFailIcon />}

                                    {pilgrim.pilgrim.is_npi_valid === null && <NpiCheckPendingIcon />}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {pilgrim.agency_name ? (
                                    <Badge variant="outline">{pilgrim.agency_name}</Badge>
                                  ) : (
                                    <Badge variant="pending">Non assigné</Badge>
                                  )}
                                </TableCell>
                                {!isReadOnly && (
                                  <TableCell>
                                    <div className="flex gap-2 items-center">
                                      <Select
                                        value={pilgrim.agency?.id || ''}
                                        onValueChange={(agencyId) => handleSelectAgency(pilgrim, agencyId)}
                                      >
                                        <SelectTrigger className="w-[200px]">
                                          <SelectValue placeholder="Choisir une agence" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {agencies?.map((agency) => (
                                            <SelectItem key={agency.id} value={agency.id}>
                                              {agency.name}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </TableCell>
                                )}
                                <TableCell>{formatDate(pilgrim.created)}</TableCell>
                                {!isReadOnly && (
                                  <TableCell className="flex justify-end">
                                    <ButtonGroup>
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button variant="outline" size="icon" aria-label="More Options">
                                            <MoreHorizontalIcon />
                                          </Button>
                                        </DropdownMenuTrigger>

                                        <DropdownMenuContent align="end" className="w-52">
                                          <DropdownMenuGroup>
                                            <DropdownMenuItem onClick={() => handleEditPilgrim(pilgrim)}>
                                              <Pen />
                                              Modifier
                                            </DropdownMenuItem>
                                          </DropdownMenuGroup>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </ButtonGroup>
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          })}
                        </TableBody>

                        {totalPages > 1 && (
                          <TableFooter>
                            <TableRow>
                              <TableCell colSpan={isReadOnly ? 4 : 7}>
                                <div className="flex items-center justify-between mt-6 text-sm text-gray-600 ">
                                  <p className="font-medium">
                                    Page {currentPage} sur {totalPages}
                                  </p>

                                  <div className="flex gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="rounded-lg"
                                      onClick={() => changePage(data.agency_ifu, currentPage - 1, totalPages)}
                                      disabled={currentPage === 1}
                                    >
                                      Précédent
                                    </Button>

                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="rounded-lg"
                                      onClick={() => changePage(data.agency_ifu, currentPage + 1, totalPages)}
                                      disabled={currentPage === totalPages}
                                    >
                                      Suivant
                                    </Button>
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          </TableFooter>
                        )}
                      </Table>
                    </div>
                  </div>
                );
              })}
          </div>
        </>
      )}

      {!isReadOnly && (
        <AssignConfirmationModal
          isOpen={confirmDialog.open}
          isDisabled={isLoading}
          onOpenChange={(open) => {
            if (!open) {
              setConfirmDialog({ open: false, pilgrim: null, agency: null });
            } else {
              setConfirmDialog({ ...confirmDialog, open });
            }
          }}
          pilgrim={confirmDialog.pilgrim}
          agency={confirmDialog.agency}
          onConfirm={confirmAssignment}
        />
      )}

      {!isReadOnly && agencySelectionModal && (
        <AgencySelectionModal
          isOpen={agencySelectionModal}
          onOpenChange={setAgencySelectionModal}
          isDisabled={isLoading}
          agencies={agencies}
          pilgrimsNumber={selectedPilgrims.length}
          onConfirm={(selectedAgency) => {
            if (!isReadOnly && !isLoading) assignAgency({ pilgrimIds: selectedPilgrims, agencyId: selectedAgency });
          }}
        />
      )}

      {!isReadOnly && openEditModal && (
        <EditTripModal
          isOpened={openEditModal}
          pilgrimData={selectedItem}
          pilgrimEligibilityMinAge={pilgrimEligibilityMinAge}
          categories={categories}
          registrationDeposit={registrationDeposit}
          isPreviousCampaignPilgrim
          trackCampaignWrite
          closeModal={() => setOpenEditModal(false)}
        />
      )}
    </Page>
  );
};

AgencyAssignationForCampaign.propTypes = {
  campaign: PropTypes.shape({
    params: PropTypes.object.isRequired,
    scope: PropTypes.array.isRequired,
    isReadOnly: PropTypes.bool.isRequired,
    isHistoricalCampaign: PropTypes.bool.isRequired,
  }).isRequired,
  search: PropTypes.string.isRequired,
  setSearch: PropTypes.func.isRequired,
  debouncedSearch: PropTypes.string.isRequired,
};

export default AgencyAssignation;
